import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useSwitchDepartment } from '@/api/hooks';
import type { Me } from '@/api/session';
import { CONTROL_ROOM, departmentScope, type Scope } from './nav';

/**
 * Where a person is working (ADR-0140; the package's `buildScopes`): one
 * department at a time, every read scoped to it by the session, or the
 * superadmin's Control room, which is oversight of every department and the
 * work of none. The Control room is this browser's convenience and never
 * leaves it: the session's department stays what it was, so nothing read
 * there is any wider than the session allows, and no department page opens
 * in it (`routeAllowed`), since it would read that session department.
 *
 * The list is last-used first, remembered per person in this browser
 * (Iryna landed in Finance once and her mail looked missing: she was in the
 * wrong room). A superadmin with no remembered choice opens in the Control
 * room; anyone else opens in the first department they lead.
 */

interface Remembered {
  control?: boolean;
  recent?: string[];
}

const key = (me: Me) => `norum:scope:${me.humanId}`;

function remembered(me: Me): Remembered | null {
  try {
    const raw = localStorage.getItem(key(me));
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<Remembered> & { platform?: boolean };
    // Stored before the view was the Control room, the choice was `platform`;
    // and whatever is stored is only a convenience, so it is read defensively.
    return {
      control: value.control ?? value.platform === true,
      recent: Array.isArray(value.recent) ? value.recent.filter((d): d is string => typeof d === 'string') : [],
    };
  } catch {
    return null;
  }
}

function remember(me: Me, value: Remembered): void {
  try {
    localStorage.setItem(key(me), JSON.stringify(value));
  } catch {
    // A private window, or storage refused: the choice lasts this visit.
  }
}

export interface ScopeState {
  scope: Scope;
  /** The scopes this person may switch into, last used first. */
  options: Scope[];
  /**
   * Switch. A department posts `/auth/department`; the Control room is this
   * browser's alone. `then` runs once the switch has landed, so a page
   * opened after it reads the new department, never the old one.
   */
  pick: (scope: Scope, then?: () => void) => void;
  switching: boolean;
  /** The last switch was refused: the department is not one of theirs. */
  failed: boolean;
}

const Ctx = createContext<ScopeState | null>(null);

function ordered(me: Me, recent: readonly string[]): string[] {
  const names = [...new Set(me.departments)];
  const rank = (d: string) => {
    const at = recent.indexOf(d);
    return at < 0 ? Number.MAX_SAFE_INTEGER : at;
  };
  return names.sort((a, b) => rank(a) - rank(b) || names.indexOf(a) - names.indexOf(b));
}

export function ScopeProvider({ me, children }: { me: Me; children: ReactNode }) {
  const location = useLocation();
  const saved = useMemo(() => remembered(me), [me]);
  const [control, setControl] = useState<boolean>(() => me.isSuperadmin && (saved ? saved.control === true : true));
  const [recent, setRecent] = useState<string[]>(() => saved?.recent ?? []);
  const switching = useSwitchDepartment();
  const landed = useRef(false);

  // Land in the remembered department, or the first one led, once.
  useEffect(() => {
    if (landed.current) return;
    landed.current = true;
    // A deep link chooses its destination through RouteGuard, not a competing
    // remembered-department request from the initial landing effect.
    if (!['/', '/today', '/workspace'].includes(location.pathname)) return;
    const wanted = saved?.recent?.[0] ?? (me.leads.includes(me.department) ? me.department : me.leads[0]);
    if (wanted && wanted !== me.department && me.departments.includes(wanted)) switching.mutate(wanted);
  }, []);

  const pick = useCallback(
    (next: Scope, then?: () => void) => {
      if (next.kind === 'control') {
        if (!me.isSuperadmin) return;
        setControl(true);
        remember(me, { control: true, recent });
        then?.();
        return;
      }
      if (!me.departments.includes(next.name)) return;
      const commit = () => {
        const nextRecent = [next.name, ...recent.filter((d) => d !== next.name)].slice(0, 12);
        setControl(false);
        setRecent(nextRecent);
        remember(me, { control: false, recent: nextRecent });
        then?.();
      };
      if (next.name !== me.department) switching.mutate(next.name, { onSuccess: commit });
      else commit();
    },
    [me, recent, switching],
  );

  const value = useMemo<ScopeState>(
    () => ({
      scope: control && me.isSuperadmin ? CONTROL_ROOM : departmentScope(me.department),
      options: [...(me.isSuperadmin ? [CONTROL_ROOM] : []), ...ordered(me, recent).map(departmentScope)],
      pick,
      switching: switching.isPending,
      failed: switching.isError,
    }),
    [control, me, recent, pick, switching.isPending, switching.isError],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The scope in view; outside the provider (a test's bare rail), the session's department. */
export function useScope(me: Me): ScopeState {
  const ctx = useContext(Ctx);
  const switching = useSwitchDepartment();
  return (
    ctx ?? {
      scope: departmentScope(me.department),
      options: me.departments.map(departmentScope),
      pick: (next, then) => {
        if (next.kind === 'department' && next.name !== me.department) switching.mutate(next.name, then ? { onSuccess: () => then() } : undefined);
        else then?.();
      },
      switching: switching.isPending,
      failed: switching.isError,
    }
  );
}

/**
 * Whether the page in view is read in a department rather than the Control
 * room: a list that is company-wide in the Control room is that
 * department's alone here (`here` on the read).
 */
export function useInDepartment(me: Me): boolean {
  return useScope(me).scope.kind === 'department';
}
